<script setup lang="ts">
import dayjs from 'dayjs'
import { round } from 'lodash'
import { ProgressBar, useToast } from 'primevue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useAccountStore } from '@/store/account'
import { useStakeStore } from '@/store/stake'
import { getImageUrl, getTxTypeStr } from '@/utils'
import { networks } from '@/utils/config'

const props = defineProps<{
  item: any
}>()

const accountStore = useAccountStore()
const stakeStore = useStakeStore()
const toast = useToast()
const waitTime = ref(0)
const currentTime = ref(dayjs()) // 添加响应式当前时间

const account = computed(() => accountStore.account)
const isCcip = computed(() => accountStore.contractHelper?.adapter.isCcip)
const txList = computed(() => stakeStore.txList)
const currentNetwork = computed(() => networks.find(item => item.id === props.item.chainId))
const toNetwork = computed(() => networks.find(item => item.id !== props.item.chainId))
const withdrawStatus = computed(() => accountStore.withdrawStatus)
const isWaitingWithdraw = computed(() => {
  return withdrawStatus.value.some(item => !item.iswithdrawed)
})

const endTime = computed(() => {
  // switch (props.item.type) {
  //   case 'stake':
  //     return '20 mins'
  //   case 'unstake':
  //     return dayjs(props.item.createdAt).add(11, 'days').format('MM/DD/YYYY')
  //   case 'instantUnstake':
  //     return isCcip.value ? '1 hours' : '5 mins'
  //   case 'bridge':
  //     return isCcip.value ? '1 hours' : '5 mins'
  //   default:
  //     return ''
  // }
  const startTime = dayjs(props.item.createdAt)

  switch (props.item.type) {
    case 'stake':
      return startTime.add(isCcip.value ? 50 : 1, 'minutes')
    case 'withdraw':
      return startTime.add(11, 'days')
    case 'instantUnstake':
      return startTime.add(isCcip.value ? 60 * 24 : 1, 'minutes')
    case 'bridge':
      return startTime.add(isCcip.value ? 40 : 5, 'minutes')
    default:
      return startTime
  }
})

const isShow = computed(() => {
  // 如果是 withdraw 类型且正在等待 withdraw，则不显示
  if (props.item.type === 'withdraw' && isWaitingWithdraw.value) {
    return false
  }

  return waitTime.value > 0
})

const txTypeStr = computed(() => getTxTypeStr(props.item.type))

const isWASTR = computed(() => props.item.type === 'stake')

// const percent = computed(() => {
//   const endTime = dayjs(blockTime.value)
//   const startTime = endTime.subtract(11, 'day')
//   const now = dayjs()

//   // 如果当前时间超过结束时间，返回100%
//   if (now.isAfter(endTime))
//     return 100
//   // 如果当前时间在开始时间之前，返回0%
//   if (now.isBefore(startTime))
//     return 0

//   // 计算进度
//   const total = endTime.diff(startTime, 'second')
//   const passed = now.diff(startTime, 'second')
//   return Math.floor((passed / total) * 100)
// })

const percent = computed(() => {
  const startTime = dayjs(props.item.createdAt)
  const now = currentTime.value // 使用响应式时间

  // 如果当前时间超过结束时间，返回100%
  if (now.isAfter(endTime.value))
    return 100
  // 如果当前时间在开始时间之前，返回0%
  if (now.isBefore(startTime))
    return 0

  // 计算总时长和已过时长（秒）
  const totalDuration = endTime.value.diff(startTime, 'second')
  const passedDuration = now.diff(startTime, 'second')

  return Math.floor((passedDuration / totalDuration) * 100)
})

const waitTimeShow = computed(() => {
  if (waitTime.value < 59) {
    return `${waitTime.value} mins`
  }
  else if (waitTime.value < 60 * 24) {
    return `${round(waitTime.value / 60, 0)} hours`
  }
  else {
    return `${round(waitTime.value / 60 / 24, 0)} days`
  }
})
let timer: NodeJS.Timer | null = null
let statusTimer: NodeJS.Timer | null = null
function startTimer() {
  // 每分钟检查并减少等待时间
  timer = setInterval(() => {
    if (waitTime.value > 0) {
      waitTime.value--
      currentTime.value = dayjs() // 更新当前时间
      console.log('Remaining time:', waitTime.value)
    }

    if (waitTime.value === 0) {
      // 触发特定逻辑
      handleTimeUp()
      // 清除定时器
      if (timer) {
        clearInterval(Number(timer))
        timer = null
      }
    }
  }, 60 * 1000) // 每分钟执行一次
}

function getTxStatusTimer() {
  // 每分钟检查并减少等待时间
  statusTimer = setInterval(() => {
    if (waitTime.value > 0) {
      getTxStatus()
    }
  }, 60 * 1000) // 每分钟执行一次
}

async function getTxStatus() {
  if (!account.value) {
    return
  }
  const status = await accountStore.getTxStatus(props.item.hash, props.item.type)
  console.log(status, 'status')
  if (status.isSuccess) {
    handleTimeUp()
  }
}

function handleTimeUp() {
  // 在这里添加时间到达时需要执行的逻辑
  console.log('Time is up!')
  waitTime.value = 0
  toast.add({
    severity: 'success',
    summary: `Success`,
    detail: `${props.item.type} is completed`,
    life: 10000,
  })
}

onMounted(() => {
  const now = dayjs()
  const diff = endTime.value.diff(now, 'minute')
  waitTime.value = diff
  if (isShow.value) {
    startTimer()
    if (isCcip.value || props.item.type === 'bridge') {
      getTxStatusTimer()
    }
  }
})

onUnmounted(() => {
  if (timer) {
    clearInterval(Number(timer))
    timer = null
  }
  if (statusTimer) {
    clearInterval(Number(statusTimer))
    statusTimer = null
  }
})

function handleDelete() {
  console.log('handleDelete')
  const list = txList.value.filter(item => item.hash !== props.item.hash)
  stakeStore.changeTxList(list)
}
</script>

<template>
  <div v-if="isShow" class="rounded-[12px] border-2 border-black bg-white p-5">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <img
          class="h-6 w-6"
          :src="getImageUrl(isWASTR ? 'token/astr.svg' : 'token/stastr.svg')"
          alt=""
        />
        <span>{{ props.item.amount }} {{ isWASTR ? 'ASTR' : 'wstASTR' }}</span>
        <a :href="props.item.exploreUrl" target="_blank">
          <i class="pi pi-external-link cursor-pointer text-xs font-bold text-[#2F80ED]"></i>
        </a>
      </div>
      <i class="pi pi-times cursor-pointer" style="font-size: 1rem" @click="handleDelete"></i>
    </div>

    <div class="mt-4 flex items-center justify-between">
      <div v-if="props.item.type === 'bridge'" class="flex items-center gap-2">
        <img
          class="w-3 lg:w-4"
          :src="
            getImageUrl(`token/${currentNetwork?.nativeCurrency?.name.toLocaleLowerCase()}.svg`)
          "
        />
        <i class="pi pi-arrow-right text-[10px] font-medium text-brand-900"></i>
        <img
          class="w-3 lg:w-4"
          :src="getImageUrl(`token/${toNetwork?.nativeCurrency?.name.toLocaleLowerCase()}.svg`)"
        />
        <span class="text-xs">{{ txTypeStr }}</span>
      </div>
      <div v-else class="flex items-center gap-2">
        <img
          class="w-3 lg:w-4"
          :src="
            getImageUrl(`token/${currentNetwork?.nativeCurrency?.name.toLocaleLowerCase()}.svg`)
          "
        />
        <span class="text-xs">{{ txTypeStr }}</span>
      </div>
      <div class="rounded-[8px] bg-[#E9F7EF] px-3 py-1 text-xs font-medium text-[#11A24D]">
        In Progress
      </div>
    </div>

    <div class="mt-3 w-full lg:w-[257px]">
      <div class="mb-2 flex justify-between">
        <div v-if="props.item.type === 'withdraw'" class="p3 text-brand-500">
          Processing
        </div>
        <div v-else class="p3 text-brand-500">
          Est. remain
        </div>
        <div class="p3 text-brand-900">
          <div v-if="props.item.type === 'withdraw'" class="flex items-center gap-2">
            <i class="pi pi-spin pi-spinner text-sm"></i>
          </div>
          <span v-else>{{ waitTimeShow }}</span>
        </div>
      </div>
      <ProgressBar
        v-if="waitTime > 5"
        :value="percent"
        :show-value="false"
        :pt="{ root: 'h-2', value: 'bg-[#11A24D]' }"
      />
    </div>

    <!-- Bridge 说明信息 -->
    <div v-if="props.item.type === 'bridge'" class="mt-4 rounded-lg bg-gray-100 p-3">
      <div class="flex items-start gap-2">
        <i class="pi pi-info-circle mt-0.5 text-sm text-gray-600"></i>
        <p class="max-w-[200px] text-xs leading-relaxed text-gray-700">
          Once bridging request is complete, your unstake request will enter the queue. Please wait patiently, we'll handle the rest.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less"></style>
